/**
 * Shared severity badge for the Shadow AI Discovery UI. Pure string helpers
 * live in `awsDiscoveryFormat.ts`; `truncate` is re-exported here for callers.
 */

import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import type { PostureSeverity } from "@/types/awsDiscovery";

const SEVERITY_STYLES: Record<PostureSeverity, string> = {
  critical: "bg-red-600 text-white hover:bg-red-700 border-transparent",
  high: "bg-orange-500 text-white hover:bg-orange-600 border-transparent",
  medium: "bg-amber-500 text-white hover:bg-amber-600 border-transparent",
  low: "bg-yellow-400 text-black hover:bg-yellow-500 border-transparent",
  info: "bg-muted text-muted-foreground border-transparent",
};

const SEVERITY_LABELS: Record<PostureSeverity, string> = {
  critical: "Critical",
  high: "High",
  medium: "Medium",
  low: "Low",
  info: "Info",
};

/**
 * Severity badge for a posture flag. Falls back to the `info` style for any
 * unexpected severity string from the backend.
 */
export function PostureFlagBadge({
  severity,
  className,
}: {
  severity: PostureSeverity | string;
  className?: string;
}) {
  const key = (severity in SEVERITY_STYLES ? severity : "info") as PostureSeverity;
  return (
    <Badge className={cn("text-xs", SEVERITY_STYLES[key], className)}>
      {SEVERITY_LABELS[key]}
    </Badge>
  );
}
